import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { v4 as uuidv4 } from 'uuid';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';

import { IJudgmentCourt } from '../../../APIPackage/CatalogsModule/interfaces/IJudgmentCourt';
import { catalogCreateJudgmentCourt } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { IKnmDistrictSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { IKnmJudgmentCourtLevelSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmJudgmentCourtLevelSelectOption';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { SelectInput } from '../../../UIPackage/MainModule/components/SelectInput';
import { TextInput } from '../../../UIPackage/MainModule/components/TextInput';
import styles from './Judgment.module.scss';

interface ITemplateDecisionJudgmentCreateCourtModalProps {
  visible: boolean;
  onClose: () => void;
  watchOnly: boolean;
  selectedRegion: IKnmDistrictSelectOption | undefined;
  judgmentCourtLevelSelectOptions: IKnmJudgmentCourtLevelSelectOption[] | undefined;
  districtSelectOptions: IKnmDistrictSelectOption[] | undefined;
}

export const DecisionJudgmentCreateCourtModal = ({
  visible,
  onClose,
  watchOnly,
  judgmentCourtLevelSelectOptions,
  districtSelectOptions,
  selectedRegion,
}: ITemplateDecisionJudgmentCreateCourtModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [courtName, setCourtName] = useState<string | undefined>(undefined);
  const [courtRegion, setCourtRegion] = useState<IKnmDistrictSelectOption | undefined>(undefined);
  const [courtLevel, setCourtLevel] = useState<IKnmJudgmentCourtLevelSelectOption | undefined>(undefined);

  useEffect(() => {
    setCourtName(undefined);
    setCourtRegion(selectedRegion);
    setCourtLevel(undefined);
  }, [visible]);

  const handleCourtNameChange = useCallback((v: ChangeEvent<HTMLInputElement>) => {
    setCourtName(v.target.value);
  }, []);

  const handleCourtRegionChange = useCallback((v: IKnmDistrictSelectOption) => {
    setCourtRegion(v);
  }, []);

  const handleCourtLevelChange = useCallback((v: IKnmJudgmentCourtLevelSelectOption) => {
    setCourtLevel(v);
  }, []);

  const createCourt = () => {
    if (courtName) {
      const newCourt: IJudgmentCourt = {
        id: -1,
        code: null,
        guid: uuidv4(),
        courtType: courtLevel?.code || null,
        title: courtName,
        address: null,
        web: null,
        rfRegion: courtRegion?.code?.substring(1) || null,
        erknmCourt: true,
        enabled: true,
      };

      dispatch(catalogCreateJudgmentCourt(newCourt));
      onClose();
    }
  };

  return (
    <Modal modalBodyClassName={styles.ModalBody} visible={visible} onClose={onClose}>
      <ModalHeader title="Создание нового судебного органа" sizeText="medium" color="black" />
      <ModalBody size="content" border="bottom">
        <Row>
          <FieldBlock label="Субъект Российской Федерации">
            <SelectInput
              placeholder="Выберите субъект Российской Федерации"
              options={districtSelectOptions}
              disabled={watchOnly}
              value={courtRegion}
              size="medium"
              onChange={handleCourtRegionChange}
              isSearchable
            />
            {!courtRegion && <p className="shared-input-error">Поле не может быть пустым</p>}
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Наименование суда">
            <TextInput
              disabled={watchOnly}
              placeholder="Введите наименование суда"
              value={courtName}
              onChange={handleCourtNameChange}
            />
            {!courtName && <p className="shared-input-error">Поле не может быть пустым</p>}
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Уровень суда">
            <SelectInput
              placeholder="Выберите уровень суда"
              options={judgmentCourtLevelSelectOptions}
              disabled={watchOnly}
              value={courtLevel}
              size="medium"
              onChange={handleCourtLevelChange}
            />
            {!courtLevel && <p className="shared-input-error">Поле не может быть пустым</p>}
          </FieldBlock>
        </Row>
      </ModalBody>
      <ModalActions>
        <Button
          text="Сохранить"
          id="saveButton"
          size="medium"
          disabled={watchOnly && !courtName && !courtLevel && !courtRegion}
          onClick={createCourt}
        />
        <Button text="Закрыть" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};
